// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import dynamicProto from "@microsoft/dynamicproto-js";
import { isNotNullOrUndefined, isString, newGuid, onConfigChange } from "@microsoft/applicationinsights-core-js";
import { createAsyncAllPromise, createAsyncPromise, doAwait, doAwaitResponse } from "@nevware21/ts-async";
import { batchDropNotification, getEndpointDomain, getTimeFromId, getTimeId } from "../Helpers/Utils";
import { _DYN_ADD_EVENT, _DYN_CLEAR, _DYN_CONTINUE, _DYN_CRITICAL_CNT, _DYN_DIAG_LOG, _DYN_GET_NEXT_BATCH, _DYN_GET_TIME, _DYN_INITIALIZE, _DYN_INSTRUMENTATION_KEY, _DYN_IS_AVAILABLE, _DYN_LENGTH, _DYN_MAX_CRITICAL_EVTS_DR3, _DYN_NOTIFICATION_MGR, _DYN_ONERROR, _DYN_ONSUCCESS, _DYN_PUSH, _DYN_REMOVE_EVENTS, _DYN_STORAGE_CONFIG, _DYN_SUPPORTS_SYNC_REQUES4, _DYN_TEARDOWN, _DYN_THEN, _DYN__EVENTS_TO_DROP_PER_2 } from "../__DynamicConstants";
import { IndexedDbHelper } from "./IndexDbHelper";
//TODO: move all const to one file
var EventsToDropAtOneTime = 10; // If we fail to add a new event this is the max number of events we will attempt to remove to make space
var StoreVersion = 1; // The Current version for the stored items, this will be used in the future for versioning
var OrhpanedEventThresholdInMs = 604800000; // 7 days
var UnknowniKey = "Unknown";
var ErrorMessageUnableToAddEvent = "DBError: Unable to add event";
var MaxCriticalEvtsDropCnt = 2;
export var DefaultDbName = "AIOffline"; // Db Name including the version number on the end so that if we ever have to upgrade old and new code can co-exist
export var DbVersion = 1; // The Current version of the database (Used to trigger upgrades)
export var EventObjectStoreName = "Evts";
function _getTime() {
    return new Date()[_DYN_GET_TIME /* @min:%2egetTime */]();
}
function _createDb(db) {
    // data in the same db must have same endpoint url
    if (!db.objectStoreNames.contains(EventObjectStoreName)) {
        var evtStore = db.createObjectStore(EventObjectStoreName, { keyPath: "id" });
        evtStore.createIndex("criticalCnt", "criticalCnt", { unique: false });
    }
}
function _getEvents(values) {
    var events = [];
    for (var lp = 0; lp < values[_DYN_LENGTH /* @min:%2elength */]; lp++) {
        var item = values[lp];
        if (item && item.evt) {
            events[_DYN_PUSH /* @min:%2epush */](item.evt);
        }
    }
    return events;
}
function _addEventByTime(values, newValue) {
    for (var idx = 0; idx < values[_DYN_LENGTH /* @min:%2elength */]; idx++) {
        if (newValue.tm < values[idx].tm) {
            values.splice(idx, 0, newValue);
            return;
        }
    }
    values[_DYN_PUSH /* @min:%2epush */](newValue);
}
function _getId(key, values) {
    var len = values[_DYN_LENGTH /* @min:%2elength */];
    for (var lp = 0; lp < len; lp++) {
        if (key === values[lp].id) {
            return lp;
        }
    }
    return -1;
}
function _cursorContinueEvent(cursorState, value) {
    return function (evt) {
        return cursorState[_DYN_CONTINUE /* @min:%2econtinue */]();
    };
}
function _cursorDeleteAndContinue(cursorState, value) {
    var deleteRequest = cursorState.cursor.delete();
    deleteRequest[_DYN_ONERROR /* @min:%2eonerror */] = _cursorContinueEvent(cursorState);
    deleteRequest[_DYN_ONSUCCESS /* @min:%2eonsuccess */] = _cursorContinueEvent(cursorState);
    return 1 /* CursorProcessResult.Waiting */;
}
function _getAllEvents(dbCtx, cursorQuery, cnt) {
    // Open the Db store returning the embedded promise for handling resolving and rejecting the request chain
    return dbCtx.openCursor(EventObjectStoreName, cursorQuery, function (cursorState, value, values) {
        values[_DYN_PUSH /* @min:%2epush */](value);
        if (cnt && values && values[_DYN_LENGTH /* @min:%2elength */] == cnt) {
            return 2 /* CursorProcessResult.Complete */;
        }
        return 0 /* CursorProcessResult.Continue */;
    });
}
function _deleteEvents(dbCtx, eventPrefixQuery, shouldDelete) {
    // Open the Event object store
    return dbCtx.openCursor(EventObjectStoreName, eventPrefixQuery, function (deleteCursorState, value, values) {
        if (shouldDelete(value)) {
            values[_DYN_PUSH /* @min:%2epush */](value);
            return _cursorDeleteAndContinue(deleteCursorState, value);
        }
        return 0 /* CursorProcessResult.Continue */;
    });
}
function _dropMaxTimeEvents(dbCtx, maxTime) {
    return createAsyncPromise(function (deleteEvtsResolve, deleteEvtsReject) {
        return _deleteEvents(dbCtx, null, function (value) {
            if (!value || !value.evt) {
                return true;
            }
            var addedTime = getTimeFromId(value.id);
            var minStartTime = _getTime() + 1 - maxTime;
            if (addedTime <= minStartTime) {
                return true;
            }
            return false;
        })[_DYN_THEN /* @min:%2ethen */](function (values) {
            deleteEvtsResolve(values);
        }, deleteEvtsReject);
    });
}
function _dropEventsUpToPersistence(dbCtx, maxpriorityCnt, maxDropCnt) {
    return createAsyncPromise(function (dropEventsResolve, dropEventsReject) {
        var droppedEvents = 0;
        function _resolveWithDroppedEvents() {
            dropEventsResolve(droppedEvents);
        }
        function _dropEvent(deleteStoreCtx, dropItem) {
            return createAsyncPromise(function (deleteResolve) {
                var deleteRequest = deleteStoreCtx.store.delete(dropItem.key);
                deleteRequest[_DYN_ONSUCCESS /* @min:%2eonsuccess */] = function (evt) {
                    droppedEvents++;
                    deleteResolve();
                };
                deleteRequest[_DYN_ONERROR /* @min:%2eonerror */] = function (evt) {
                    deleteResolve();
                };
            });
        }
        function _processCandidates(dropCandidates) {
            if (dropCandidates[_DYN_LENGTH /* @min:%2elength */] === 0) {
                _resolveWithDroppedEvents();
                return;
            }
            dbCtx.openStore(EventObjectStoreName, function (deleteStoreCtx) {
                var deleteEvts = [];
                for (var lp = 0; lp < dropCandidates[_DYN_LENGTH /* @min:%2elength */]; lp++) {
                    deleteEvts[_DYN_PUSH /* @min:%2epush */](_dropEvent(deleteStoreCtx, dropCandidates[lp]));
                }
                return createAsyncAllPromise(deleteEvts)[_DYN_THEN /* @min:%2ethen */](_resolveWithDroppedEvents, _resolveWithDroppedEvents);
            });
        }
        // Get all of the candidates that we may delete
        var cursorPromise = dbCtx.openCursor(EventObjectStoreName, null, function (cursorState, value, values) {
            if (value.evt[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] <= maxpriorityCnt) {
                _addEventByTime(values, value);
                if (values[_DYN_LENGTH /* @min:%2elength */] > maxDropCnt) {
                    values.splice(values[_DYN_LENGTH /* @min:%2elength */] - 1, 1);
                }
            }
            return 0 /* CursorProcessResult.Continue */;
        });
        cursorPromise[_DYN_THEN /* @min:%2ethen */](_processCandidates, function () {
            dropEventsResolve(0);
        });
    });
}
/**
 * Class that implements storing of events using the WebStorage Api ((window||globalThis||self).localstorage, (window||globalThis||self).sessionStorage).
 */
var IndexedDbProvider = /** @class */ (function () {
    /**
     * Creates a WebStorageProvider using the provider storageType
     */
    function IndexedDbProvider(id, unloadHookContainer) {
        dynamicProto(IndexedDbProvider, this, function (_this) {
            var _indexedDb = null;
            var _dbName = null;
            var _iKey = UnknowniKey;
            var _storageId = null; // Used as a unique id so that when active on multiple tabs (threads) the apps don't corrupts each other
            var _autoClean = null;
            var _endpoint = null;
            var _storageKeyPrefix = null;
            var _maxStorageTime = null;
            var _eventDropPerTime = null;
            var _maxCriticalCnt = null;
            var _notificationManager = null;
            _this.id = id;
            _this[_DYN_INITIALIZE /* @min:%2einitialize */] = function (providerContext) {
                var diagLog = providerContext.itemCtx[_DYN_DIAG_LOG /* @min:%2ediagLog */]();
                _indexedDb = new IndexedDbHelper(diagLog);
                if (!_indexedDb[_DYN_IS_AVAILABLE /* @min:%2eisAvailable */]()) {
                    _indexedDb = null;
                    return false;
                }
                var coreConfig = providerContext.itemCtx.getCfg();
                var itemCtx = providerContext.itemCtx;
                var ikey = itemCtx.getCfg()[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */] || coreConfig[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */];
                if (!isString(ikey)) {
                    //_iKey = ikey
                    return;
                }
                _iKey = ikey;
                var storageConfig = providerContext[_DYN_STORAGE_CONFIG /* @min:%2estorageConfig */];
                _storageId = _this.id || providerContext.id || newGuid();
                _notificationManager = providerContext[_DYN_NOTIFICATION_MGR /* @min:%2enotificationMgr */];
                _endpoint = getEndpointDomain(providerContext.endpoint);
                _autoClean = !!storageConfig.autoClean;
                _storageKeyPrefix = storageConfig.storageKeyPrefix || DefaultDbName;
                _dbName = _storageKeyPrefix + "_" + DbVersion + "_" + _endpoint;
                // currently, won't handle endpoint change
                // new endpoint will open a new db
                // endpoint change will be handled at offline batch lavel
                // namePrefix should not contain any "_"
                var unloadHook = onConfigChange(storageConfig, function () {
                    _maxStorageTime = storageConfig.inStorageMaxTime || OrhpanedEventThresholdInMs; // TODO: handle 0
                    var dropNum = storageConfig[_DYN__EVENTS_TO_DROP_PER_2 /* @min:%2eEventsToDropPerTime */];
                    _eventDropPerTime = isNotNullOrUndefined(dropNum) ? dropNum : EventsToDropAtOneTime;
                    _maxCriticalCnt = storageConfig[_DYN_MAX_CRITICAL_EVTS_DR3 /* @min:%2emaxCriticalEvtsDropCnt */] || MaxCriticalEvtsDropCnt;
                });
                unloadHookContainer && unloadHookContainer.add(unloadHook);
                if (_dbName) {
                    doAwaitResponse(_this.clean(!_autoClean), function (response) {
                        _openDb(function (dbCtx) {
                            return true;
                        })[_DYN_THEN /* @min:%2ethen */](function (value) {
                            // All done, but as initialize isn't waiting just ignore
                        }, function (reason) {
                            // clear and stop using indexedDb
                            diagLog.warnToConsole("IndexedDbProvider failed to initialize - " + (reason || "<unknown>"));
                            _indexedDb = null;
                        });
                    });
                }
                return true;
            };
            _this["_getDbgPlgTargets"] = function () {
                return [_dbName, _endpoint, _maxStorageTime, _indexedDb];
            };
            /**
              * Identifies whether this storage provider support synchronious requests
             */
            _this[_DYN_SUPPORTS_SYNC_REQUES4 /* @min:%2esupportsSyncRequests */] = function () {
                return false;
            };
            /**
             * Get all of the currently cached events( with given number) from the storage mechanism
             */
            _this.getAllEvents = function (cnt) {
                if (_indexedDb == null || !_indexedDb[_DYN_IS_AVAILABLE /* @min:%2eisAvailable */]()) {
                    return [];
                }
                return _openDb(function (dbCtx) {
                    return createAsyncPromise(function (allEventsResolve, allEventsReject) {
                        _getAllEvents(dbCtx, null, cnt)[_DYN_THEN /* @min:%2ethen */](function (values) {
                            //TODO: orderEvents By CriticalCnt
                            allEventsResolve(_getEvents(values));
                        }, allEventsReject);
                    });
                });
            };
            /**
             * Get all of the currently cached events( with given number) from the storage mechanism
             */
            _this[_DYN_GET_NEXT_BATCH /* @min:%2egetNextBatch */] = function () {
                if (_indexedDb == null || !_indexedDb[_DYN_IS_AVAILABLE /* @min:%2eisAvailable */]()) {
                    return [];
                }
                // Start an asynchronous set of events to access the Db, this first one will wait until all current outstanding
                // events are completed or rejected
                return _openDb(function (dbCtx) {
                    return createAsyncPromise(function (allEventsResolve, allEventsReject) {
                        _getAllEvents(dbCtx, null, 1)[_DYN_THEN /* @min:%2ethen */](function (values) {
                            allEventsResolve(_getEvents(values));
                        }, allEventsReject);
                    });
                });
            };
            /**
             * Stores the value into the storage using the specified key.
             * @param key - The key value to use for the value
             * @param value - The actual value of the request
             */
            _this[_DYN_ADD_EVENT /* @min:%2eaddEvent */] = function (key, item, itemCtx) {
                if (_indexedDb == null || !_indexedDb[_DYN_IS_AVAILABLE /* @min:%2eisAvailable */]()) {
                    return item;
                }
                item.id = item.id || getTimeId();
                item[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] = item[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] || 0;
                return _openDb(function (dbCtx) {
                    var eventKey = key || item.id;
                    var dbItem = {
                        id: eventKey,
                        evt: item,
                        tm: _getTime(),
                        v: StoreVersion
                    };
                    return _addDbEvent(dbCtx, dbItem, true);
                });
            };
            /**
             * Removes the values
             * @param evts - */
            _this[_DYN_REMOVE_EVENTS /* @min:%2eremoveEvents */] = function (evts) {
                if (_indexedDb == null || !_indexedDb[_DYN_IS_AVAILABLE /* @min:%2eisAvailable */]()) {
                    return [];
                }
                var removedEvents = [];
                return createAsyncPromise(function (removeEventsResolve, removeEventsReject) {
                    // Open the Db store, this will handling resolving and rejecting this promise via the passed resolve/reject functions
                    _openDb(function (dbCtx) {
                        return dbCtx.openCursor(EventObjectStoreName, null, function (cursorState, value, values) {
                            if (_getId(value.id, evts) !== -1) {
                                var deleteRequest = cursorState.cursor.delete();
                                deleteRequest[_DYN_ONERROR /* @min:%2eonerror */] = _cursorContinueEvent(cursorState);
                                deleteRequest[_DYN_ONSUCCESS /* @min:%2eonsuccess */] = function () {
                                    removedEvents[_DYN_PUSH /* @min:%2epush */](value.evt);
                                    cursorState[_DYN_CONTINUE /* @min:%2econtinue */]();
                                };
                                return 1 /* CursorProcessResult.Waiting */;
                            }
                            return 0 /* CursorProcessResult.Continue */;
                        });
                    })[_DYN_THEN /* @min:%2ethen */](function () {
                        removeEventsResolve(removedEvents); // Resolve the RemoveEvents call promise
                    }, function (reason) {
                        removeEventsResolve(removedEvents); // Resolve the RemoveEvents call promise
                    });
                });
            };
            /**
             * Removes all entries from the storage provider and returns them as part of the response, if there are any.
             */
            _this[_DYN_CLEAR /* @min:%2eclear */] = function (disable) {
                if (_indexedDb == null || !_indexedDb[_DYN_IS_AVAILABLE /* @min:%2eisAvailable */]() || !!disable) {
                    return [];
                }
                return createAsyncPromise(function (clearResolve, clearReject) {
                    _openDb(function (dbCtx) {
                        //delete all evts
                        return _deleteEvents(dbCtx, null, function (value) {
                            return true;
                        });
                    })[_DYN_THEN /* @min:%2ethen */](function (values) {
                        clearResolve(_getEvents(values));
                    }, function (reason) {
                        clearResolve([]);
                    });
                });
            };
            _this.clean = function (disable) {
                if (_indexedDb == null || !_indexedDb[_DYN_IS_AVAILABLE /* @min:%2eisAvailable */]() || !!disable) {
                    return false;
                }
                return createAsyncPromise(function (cleanResolve, cleanReject) {
                    _openDb(function (dbCtx) {
                        if (dbCtx.isNew) {
                            return [];
                        }
                        return _dropMaxTimeEvents(dbCtx, _maxStorageTime);
                    })[_DYN_THEN /* @min:%2ethen */](function (value) {
                        var cnt = value && value[_DYN_LENGTH /* @min:%2elength */];
                        if (_notificationManager && cnt) {
                            batchDropNotification(_notificationManager, cnt, 3 /* eBatchDiscardedReason.MaxInStorageTimeExceeded */);
                        }
                        cleanResolve(cnt && cnt > 0);
                    }, function (reason) {
                        cleanResolve(false);
                    });
                });
            };
            /**
             * Shuts-down the telemetry plugin. This is usually called when telemetry is shut down.
             * This attempts to update the lastAccessTime for any storedDb
             */
            _this[_DYN_TEARDOWN /* @min:%2eteardown */] = function () {
                if (_indexedDb) {
                    _indexedDb.closeDb(_dbName);
                }
            };
            /**
             * Schedules the opening of the database if not already open
             */
            function _openDb(processFunc) {
                function _handleDbUpgrade(dbCtx) {
                    return createAsyncPromise(function (createResolve, createReject) {
                        try {
                            _createDb(dbCtx.db);
                        }
                        catch (e) {
                            createReject(e);
                        }
                        createResolve();
                    });
                }
                function _handleDbOpen(dbCtx) {
                    return createAsyncPromise(function (openResolve, openReject) {
                        var providerCtx = {
                            iKey: _iKey,
                            id: _endpoint,
                            storageId: _storageId
                        };
                        dbCtx.ctx = providerCtx;
                        doAwait(processFunc(dbCtx), openResolve, openReject);
                    });
                }
                return _indexedDb.openDb(_dbName, DbVersion, _handleDbOpen, _handleDbUpgrade);
            }
            function _addDbEvent(dbCtx, dbItem, doRetry) {
                return createAsyncPromise(function (addEventResolve, addEventReject) {
                    function dropEvents(droppedFunc) {
                        // Try and clear space by dropping the Normal level events, note dropEvents promise never rejects
                        _dropEventsUpToPersistence(dbCtx, _maxCriticalCnt, _eventDropPerTime)[_DYN_THEN /* @min:%2ethen */](function (droppedCount) {
                            if (_notificationManager && droppedCount) {
                                batchDropNotification(_notificationManager, droppedCount, 2 /* eBatchDiscardedReason.CleanStorage */);
                            }
                            droppedFunc(droppedCount);
                        }, function (reason) {
                            // won't throw errors here, unblock following process
                            droppedFunc(0);
                        });
                    }
                    function _insertNewEvent() {
                        dbCtx.openStore(EventObjectStoreName, function (storeCtx) {
                            var request = storeCtx.store.put(dbItem);
                            request[_DYN_ONSUCCESS /* @min:%2eonsuccess */] = function (evt) {
                                addEventResolve(dbItem.evt);
                            };
                            request[_DYN_ONERROR /* @min:%2eonerror */] = function (evt) {
                                if (!doRetry) {
                                    addEventReject(new Error(ErrorMessageUnableToAddEvent)); // Reject the calling promise
                                    return;
                                }
                                function _retryAddEvent(dropCount) {
                                    if (dropCount === 0) {
                                        // We failed to free up space so just reject
                                        addEventReject(new Error(ErrorMessageUnableToAddEvent)); // Reject the calling promise
                                    }
                                    // Retry sending the event
                                    _addDbEvent(dbCtx, dbItem, false)[_DYN_THEN /* @min:%2ethen */](function (theItem) {
                                        addEventResolve(dbItem.evt); // Resolve the calling add Event promise
                                    }, function () {
                                        addEventReject(new Error(ErrorMessageUnableToAddEvent)); // Reject the calling promise
                                    });
                                }
                                dropEvents(function (droppedCount) {
                                    if (droppedCount > 0) {
                                        // We dropped some events so lets try adding the item again
                                        _retryAddEvent(droppedCount);
                                    }
                                    else {
                                        // We have already tried to remove all we can
                                        addEventReject(new Error(ErrorMessageUnableToAddEvent)); // Reject the calling promise
                                    }
                                });
                            };
                        }).catch(function (e) {
                            addEventReject(e);
                        });
                    }
                    _insertNewEvent();
                });
            }
        });
    }
    /**
     * Initializes the provider using the config
     * @param providerContext - The provider context that should be used to initialize the provider
     * @returns True if the provider is initialized and available for use otherwise false
     */
    IndexedDbProvider.prototype.initialize = function (providerContext) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Identifies whether this storage provider support synchronous requests
    */
    IndexedDbProvider.prototype.supportsSyncRequests = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Get all of the currently cached events from the storage mechanism
     */
    IndexedDbProvider.prototype.getNextBatch = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Get all of the currently cached events from the storage mechanism
     */
    IndexedDbProvider.prototype.getAllEvents = function (cnt) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Stores the value into the storage using the specified key.
     * @param key - The key value to use for the value
     * @param evt - The actual event of the request
     * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
     * can optionally use this to access the current core instance or define / pass additional information
     * to later plugins (vs appending items to the telemetry item)
     */
    IndexedDbProvider.prototype.addEvent = function (key, evt, itemCtx) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Removes the value associated with the provided key
     * @param evts - The events to be removed
     */
    IndexedDbProvider.prototype.removeEvents = function (evts) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Removes all entries from the storage provider, if there are any.
     */
    IndexedDbProvider.prototype.clear = function (disable) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Removes all entries with stroage time longer than inStorageMaxTime from the storage provider
     */
    IndexedDbProvider.prototype.clean = function (disable) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return;
    };
    /**
     * Shuts-down the telemetry plugin. This is usually called when telemetry is shut down.
     */
    IndexedDbProvider.prototype.teardown = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    return IndexedDbProvider;
}());
export { IndexedDbProvider };
//# sourceMappingURL=IndexDbProvider.js.map